import { Link } from "react-router-dom";
import { useSeo } from "@/lib/useSeo";
import { usePageCms } from "@/lib/usePageCms";
import { PageHero, SectionHeading } from "@/components/site/PageHero";
import { LeadershipSection } from "@/components/site/LeadershipSection";
import { AwardsSection } from "@/components/site/AwardsSection";
import { Button } from "@/components/ui/button";
import { Compass, MapPin, Handshake, Leaf, LifeBuoy, ShieldCheck, MessageCircle } from "lucide-react";
import { useConfig, waLink } from "@/context/ConfigContext";

const ICONS = { "map-pin": MapPin, compass: Compass, handshake: Handshake, leaf: Leaf, "life-buoy": LifeBuoy, "shield-check": ShieldCheck };
const FB = {
  overline: "About Us", title: "The Depth of Maharashtra, Delivered",
  subtitle: "A destination management partner built for travel professionals.",
  image: "https://images.unsplash.com/photo-1671345642364-82d51a8a23b8?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000",
  seo_title: "About Maha Sampada | Maharashtra Destination Management Company",
  seo_description: "Maha Sampada is a Maharashtra DMC and a venture of Turekar Global Private Limited, helping travel partners deliver the true depth of Maharashtra.",
};

function Cta({ cta, className, ...rest }) {
  if (!cta || cta.enabled === false || !(cta.label || "").trim() || !(cta.href || "").trim()) return null;
  const btn = <Button {...rest}>{cta.label}</Button>;
  return cta.href.startsWith("/") ? <Link to={cta.href} className={className}>{btn}</Link> : <a href={cta.href} target="_blank" rel="noopener noreferrer" className={className}>{btn}</a>;
}

export default function About() {
  const { hero, sections } = usePageCms("about", FB);
  useSeo({ title: hero.seo_title || FB.seo_title, description: hero.seo_description || FB.seo_description });
  const config = useConfig();

  const R = {
    statement: (s) => (
      <section key={s.section_id} data-testid="about-purpose-section" className="mx-auto max-w-4xl px-4 py-20 text-center sm:px-6">
        {s.overline && <div className="overline text-saffron">{s.overline}</div>}
        <div className="mt-6 font-display text-2xl leading-relaxed text-charcoal sm:text-3xl" data-testid="about-purpose" dangerouslySetInnerHTML={{ __html: s.intro_html || "" }} />
      </section>
    ),
    pillars: (s) => (
      <section key={s.section_id} data-testid="about-approach-section" className="bg-white">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6">
          <SectionHeading overline={s.overline} title={s.heading} center />
          <div className="mt-12 grid gap-6 sm:grid-cols-2">
            {(s.cards || []).filter((c) => c.visible !== false).map((p, i) => { const Icon = ICONS[p.icon] || MapPin; return (
              <div key={i} className="hover-lift rounded-2xl border border-gold/25 bg-cream p-8">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-saffron/10 text-saffron"><Icon className="h-6 w-6" /></div>
                <h3 className="mt-5 font-display text-xl font-semibold text-charcoal">{p.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{p.text}</p>
              </div>
            ); })}
          </div>
        </div>
      </section>
    ),
    leadership: (s) => (
      <LeadershipSection key={s.section_id} overline={s.overline} heading={s.heading} records={s.records} />
    ),
    intro: (s) => (
      <section key={s.section_id} data-testid="about-operational-section" className="mx-auto max-w-7xl px-4 py-20 sm:px-6">
        <div className="grid items-center gap-12 lg:grid-cols-2">
          {s.image && <div className="arch-soft h-[420px] img-zoom overflow-hidden"><img src={s.image} alt={s.image_alt || ""} className="h-full w-full object-cover" /></div>}
          <div>
            <SectionHeading overline={s.overline} title={s.heading} />
            {s.body && <p className="mt-6 text-base leading-relaxed text-muted-foreground">{s.body}</p>}
            <Cta cta={s.cta} className="mt-8 inline-block rounded-full bg-maroon px-7 font-accent font-semibold text-white hover:bg-maroon/90" />
          </div>
        </div>
      </section>
    ),
    parent: (s) => (
      <section key={s.section_id} data-testid="about-parent-section" className="bg-forest">
        <div className="mx-auto max-w-4xl px-4 py-16 text-center sm:px-6">
          {s.overline && <div className="overline text-gold">{s.overline}</div>}
          <h2 className="mt-3 font-display text-3xl font-bold text-cream sm:text-4xl">{s.heading}</h2>
          {s.body && <p className="mx-auto mt-4 max-w-2xl text-sm text-cream/85">{s.body}</p>}
          <Cta cta={s.cta} className="mt-8 inline-block rounded-full bg-saffron px-8 font-accent font-semibold text-white hover:bg-saffron-dark" />
        </div>
      </section>
    ),
    cta: (s) => (
      <section key={s.section_id} data-testid="about-final-cta-section" className="relative overflow-hidden bg-forest">
        <div className="mx-auto max-w-4xl px-4 py-20 text-center sm:px-6">
          <h2 className="font-display text-3xl font-bold text-cream sm:text-5xl">{s.heading}</h2>
          {s.body && <p className="mx-auto mt-5 max-w-xl text-base text-cream/85">{s.body}</p>}
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <Cta cta={s.cta} className="rounded-full bg-saffron px-8 font-accent font-semibold text-white hover:bg-saffron-dark" size="lg" />
            {s.show_whatsapp && waLink(config.whatsapp) && <a href={waLink(config.whatsapp)} target="_blank" rel="noopener noreferrer"><Button size="lg" variant="outline" className="rounded-full border-cream bg-transparent px-8 font-accent font-semibold text-cream hover:bg-cream hover:text-forest"><MessageCircle className="mr-2 h-5 w-5" />Chat on WhatsApp</Button></a>}
          </div>
        </div>
      </section>
    ),
  };

  return (
    <div>
      <PageHero overline={hero.overline} title={hero.title} subtitle={hero.subtitle} image={hero.image} crumbs={[{ label: "About Us" }]} />
      {(sections || []).map((s) => (R[s.kind] ? R[s.kind](s) : null))}
      <AwardsSection surface="about" />
    </div>
  );
}
